import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Column,
} from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

import { CustomIntegrationProviderEnum } from 'src/graphql/schema';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { cn } from 'src/lib/utils';

export type ICustomAssetTableRow = {
  id: string;
  title?: string;
  url: string;
};

type createProviderColumnsProps = (
  | {
      removeButton: React.ElementType;
    }
  | {
      onRemove: (key: string) => void;
    }
) & {
  provider: CustomIntegrationProviderEnum;
  showAssetCount?: boolean;
};
const createProviderColumns = (props: createProviderColumnsProps): ColumnDef<ICustomAssetTableRow, unknown>[] => {
  const columnHelper = createColumnHelper<ICustomAssetTableRow>();
  const { provider } = props;

  let columns: ColumnDef<ICustomAssetTableRow, unknown>[] = [
    columnHelper.display({
      id: 'operation',
      header: '',
      cell: (info) => {
        if ('removeButton' in props) {
          const RemoveButton = props.removeButton;

          return <RemoveButton id={info.row.original.id} />;
        }

        const record = info.row.original;
        const handleRemove = () => props.onRemove(record.id);
        return (
          <Button variant="ghost" size="icon" onClick={handleRemove} className="p-1 size-5">
            <Trash2 className="text-destructive" />
          </Button>
        );
      },
      meta: {
        headerClassName: 'w-18',
        cellClassName: 'w-18',
      },
    }),
  ];

  switch (provider) {
    case CustomIntegrationProviderEnum.GitlabServer:
    case CustomIntegrationProviderEnum.BitbucketServer:
    case CustomIntegrationProviderEnum.GithubServer:
    case CustomIntegrationProviderEnum.Beanstalk: {
      columns = [
        columnHelper.accessor('title', {
          id: 'title',
          header: ({ table }) =>
            props.showAssetCount ? `Repository (${table.getRowModel().rows.length})` : 'Repository title',
          cell: (info) => info.row.original.title,
          meta: {
            headerClassName: 'w-1/2',
            cellClassName: 'w-1/2',
            placeholder: 'Repository title',
          },
        }) as ColumnDef<ICustomAssetTableRow, unknown>,
        columnHelper.accessor('url', {
          id: 'url',
          header: 'Repository URL',
          cell: (info) => info.row.original.url,
          meta: {
            headerClassName: 'w-1/2',
            cellClassName: 'w-1/2',
            placeholder: 'Repository URL',
          },
        }) as ColumnDef<ICustomAssetTableRow, unknown>,
        ...columns,
      ];
      break;
    }
    case CustomIntegrationProviderEnum.Codecommit: {
      columns = [
        columnHelper.accessor('url', {
          header: ({ table }) =>
            props.showAssetCount ? `Asset URL (${table.getRowModel().rows.length})` : 'Asset URL',
          cell: (info) => info.row.original.url,
          meta: {
            placeholder: 'Asset URL',
          },
        }) as ColumnDef<ICustomAssetTableRow, unknown>,
        ...columns,
      ];
      break;
    }
    case CustomIntegrationProviderEnum.Aws: {
      columns = [
        columnHelper.accessor('url', {
          header: ({ table }) =>
            props.showAssetCount ? `Bucket name (${table.getRowModel().rows.length})` : 'Bucket name',
          cell: (info) => info.row.original.url,
          meta: {
            placeholder: 'Bucket name',
          },
        }) as ColumnDef<ICustomAssetTableRow, unknown>,
        ...columns,
      ];
      break;
    }
  }

  return columns;
};

export type Props = (
  | {
      removeButton: React.ElementType;
    }
  | {
      onRemove: (key: string) => void;
    }
) & {
  provider: CustomIntegrationProviderEnum;
  dataSource: ICustomAssetTableRow[];
  className?: string;
  classes?: {
    header?: string;
  };
  formRow?: ({ tableColumns }: { tableColumns: Column<ICustomAssetTableRow, unknown>[] }) => React.ReactNode;
  showAssetCount?: boolean;
};

const CustomAssetTable = ({ formRow, provider, dataSource, className, classes, ...rest }: Props) => {
  const table = useReactTable({
    data: dataSource,
    columns: createProviderColumns(
      'removeButton' in rest
        ? { provider, removeButton: rest.removeButton, showAssetCount: rest.showAssetCount }
        : { provider, onRemove: rest.onRemove, showAssetCount: rest.showAssetCount },
    ),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={cn('border border-border rounded-md', className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={(cn('font-bold', header.column.columnDef.meta?.headerClassName), classes?.header)}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {formRow && formRow({ tableColumns: table.getAllColumns() })}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cn(cell.column.columnDef.meta?.cellClassName)}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default CustomAssetTable;
